import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { useVendors } from '../../context/VendorContext';
import { usePaymentTerms } from '../../context/PaymentTermContext';
import { useAuth } from '../../context/AuthContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';

const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Validation schema
const schema = yup.object().shape({
  name: yup.string().required('Vendor name is required'),
  payment_term_id: yup.string().required('Payment terms is required'),
  active: yup.boolean(),
  street_address: yup.string().required('Street address is required'),
  apt_suite_unit: yup.string(),
  city: yup.string().required('City is required'),
  state: yup.string().required('State/Province is required'),
  zipcode: yup.string().required('ZIP/Postal code is required'),
  prefix_id: yup.string().required('Prefix is required'),
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  suffix: yup.string(),
  department: yup.string(),
  job_title: yup.string(),
  phone_numbers: yup.array().of(
    yup.object().shape({
      type: yup.string().required('Phone type is required'),
      number: yup.string()
        .required('Phone number is required')
        .matches(phoneRegex, 'Phone number must be in format (xxx) xxx-xxxx'),
      ext: yup.string().required('Extension is required')
    })
  ).min(1, 'At least one phone number is required'),
  emails: yup.array().of(
    yup.object().shape({
      type: yup.string().required('Email type is required'),
      address: yup.string()
        .email('Invalid email format')
        .required('Email address is required')
        .matches(emailRegex, 'Invalid email address')
    })
  ).min(1, 'At least one email address is required'),
  private_notes: yup.string(),
  order_notes: yup.string()
});

const VendorForm = () => {
  const getRoutePath = useRoutePath();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { vendors, addVendor, updateVendor, getVendorById, getVendorData } = useVendors();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const { getPaymentTerms } = usePaymentTerms();
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [prefix, setPrefix] = useState([]);
  const dataLoadedRef = useRef(false);
  const hasFetched = useRef(false);
  const [getEditData, setGetEditData] = useState(null);

  // Add phone type options
  const phoneTypeOptions = [
    { value: 'mobile', label: 'Mobile' },
    { value: 'home', label: 'Home' },
    { value: 'work', label: 'Work' },
    { value: 'other', label: 'Other' }
  ];

  // Add email type options
  const emailTypeOptions = [
    { value: 'personal', label: 'Personal' },
    { value: 'business', label: 'Business' },
    { value: 'other', label: 'Other' }
  ];

  // Add custom styles for react-select
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
  };

  // Add phone formatting function
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    if (phoneNumber.length < 4) return phoneNumber;
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Add phone input handler
  const handlePhoneInput = (e, index) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue(`phone_numbers.${index}.number`, formatted);
  };
  
  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
    
    return {
      name: data?.name || '',
      payment_term_id: data?.payment_term_id || '',
      active: data?.active ?? true,
      street_address: data?.street_address || '',
      apt_suite_unit: data?.apt_suite_unit || '',
      city: data?.city || '',
      state: data?.state || '',
      zipcode: data?.zipcode || '',
      prefix_id: data?.prefix_id || '',
      first_name: data?.first_name || '',
      last_name: data?.last_name || '',
      suffix: data?.suffix || '',
      department: data?.department || '',
      job_title: data?.job_title || '',
      phone_numbers: data?.phone_numbers || [
        { type: 'mobile', number: '', ext: '' }
      ],
      emails: data?.emails || [
        { type: 'personal', address: '' }
      ],
      private_notes: data?.private_notes || '',
      order_notes: data?.order_notes || ''
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });

  // Reset form when not in edit mode
  useEffect(() => {
    if (!id) {
      reset(getDefaultValues());
    }
  }, [id, reset, getDefaultValues]);

  const fetchVendorData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getVendorById(id);
        if (response?.status === 200 && response?.data) {
          setGetEditData(response.data);
          // Reset form with fetched data
          reset(getDefaultValues(response.data));
        }
      } catch (error) {
        console.error('Error fetching vendor data:', error);
        toast.error('Error fetching vendor data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getVendorById, showLoading, hideLoading, reset, getDefaultValues]);

  useEffect(() => {
    fetchVendorData();
  }, [fetchVendorData]);

  // Load vendor data and payment terms
  useEffect(() => {
    if (dataLoadedRef.current) return;
    
    const loadData = async () => {
      try {
        // Load payment terms
        const paymentTermsData = await getPaymentTerms();
        setPaymentTerms(paymentTermsData?.data || []);

        // Load vendor data for dropdowns
        const vendorDataResponse = await getVendorData();
        setPrefix(vendorDataResponse?.prefix || []);
 
        dataLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load form data');
      }
    };

    loadData();
  }, [id, navigate, getVendorById, getPaymentTerms, getVendorData, user, getRoutePath]);

  const handleVendorSearch = (searchTerm) => {
    if (!searchTerm) {
      setFilteredVendors([]);
      return;
    }
    const filtered = Array.isArray(vendors)
    ? vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];
    setFilteredVendors(filtered);
  };

  const addPhoneNumber = () => {
    const currentPhones = watch('phone_numbers') || [];
    setValue('phone_numbers', [
      ...currentPhones,
      { type: 'mobile', number: '', ext: '' }
    ]);
  };

  const addEmail = () => {
    const currentEmails = watch('emails') || [];
    setValue('emails', [
      ...currentEmails,
      { type: 'personal', address: '' }
    ]);
  };

  const handleRemovePhoneNumber = (index) => {
    const currentPhones = watch('phone_numbers') || [];
    if (currentPhones.length > 1) {
      setValue('phone_numbers', currentPhones.filter((_, i) => i !== index));
    } else {
      toast.error('At least one phone number is required');
    }
  };

  const handleRemoveEmail = (index) => {
    const currentEmails = watch('emails') || [];
    if (currentEmails.length > 1) {
      setValue('emails', currentEmails.filter((_, i) => i !== index));
    } else {
      toast.error('At least one email address is required');
    }
  };

  // Add this function to handle payment terms change
  const handlePaymentTermsChange = (selectedOption) => {
    setValue('payment_term_id', selectedOption?.value || '');
    trigger('payment_term_id'); // Trigger validation after value change
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    try {
      if (id) {
        // Update existing vendor
        const response = await updateVendor(id, data);
        
        if (response.status === 200) {
          toast.success('Vendor updated successfully!');
          reset();
          navigate(getRoutePath('/vendors'));
        } else {
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
        }
        setIsLoading(false);
        hideLoading();
      } else {
        // Create new vendor
        const response = await addVendor(data);
        if (response.status === 200) {
          toast.success('Vendor created successfully!');
          reset();
          navigate(getRoutePath('/vendors'));
        } else {
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
        }
        setIsLoading(false);
        hideLoading();
      }
    } catch (error) {
      setIsLoading(false);
      hideLoading();
      console.error('Error saving vendor:', error);
      toast.error('Failed to save vendor. Please try again.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <div className="mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {id ? 'Edit Vendor' : 'Create New Vendor'}
        </h1>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Vendor Information</h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Vendor Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Vendor Name <span class="text-red-500">*</span></label>
                <input
                  type="text"
                  {...register('name')}
                  onChange={(e) => {
                    handleVendorSearch(e.target.value);
                    setValue('name', e.target.value);
                    trigger('name');
                  }}
                  onFocus={() => watch('name') && setShowVendorDropdown(true)}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Vendor Name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
                {showVendorDropdown && filteredVendors.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300">
                    {filteredVendors.map((vendor) => (
                      <div
                        key={vendor.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        {vendor.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Payment Terms <span class="text-red-500">*</span></label>
               
                <Select
                  name="payment_term_id"
                  isSearchable={true}
                  value={paymentTerms
                    ?.map(term => ({ value: term.id, label: term.name }))
                    .find(option => option.value === watch('payment_term_id'))}
                  onChange={handlePaymentTermsChange}
                  options={[
                    { value: '', label: 'Choose Payment Terms' },
                    ...paymentTerms.map(term => ({
                      value: term.id,
                      label: term.name
                    }))
                  ]}
                  styles={{
                    ...customStyles,
                    control: (base, state) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: errors.payment_term_id ? '#ef4444' : '#d1d5db',
                      '&:hover': {
                        borderColor: errors.payment_term_id ? '#ef4444' : '#9ca3af'
                      }
                    })
                  }}
                  placeholder="Select Payment Terms"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.payment_term_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.payment_term_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Active</label>
                <input
                  type="checkbox"
                  {...register('active')}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                {errors.active && (
                  <p className="mt-1 text-sm text-red-600">{errors.active.message}</p>
                )}
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Street Address <span class="text-red-500">*</span></label>
                <textarea
                  {...register('street_address')}
                  rows="2"
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.street_address ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Street Address"
                />
                {errors.street_address && (
                  <p className="mt-1 text-sm text-red-600">{errors.street_address.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Apt/Suite/Unit#</label>
                <input
                  type="text"
                  {...register('apt_suite_unit')}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  placeholder="Enter Apt/Suite/Unit#"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City <span class="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...register('city')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.city ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter City"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">State/Province <span class="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...register('state')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.state ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter State/Province"
                  />
                  {errors.state && (
                    <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">ZIP/Postal Code <span class="text-red-500">*</span></label>
                <input
                  type="text"
                  {...register('zipcode')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.zipcode ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter ZIP/Postal Code"
                />
                {errors.zipcode && (
                  <p className="mt-1 text-sm text-red-600">{errors.zipcode.message}</p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Prefix</label>
                <Select
                  name="prefix_id"
                  value={
                    prefix
                    ?.map(term => ({ value: term.id, label: term.name }))
                    .find(option => option.value === watch('prefix_id'))}
                  onChange={(selectedOption) => {
                     
                    setValue('prefix_id', selectedOption?.value || '');
                    trigger('prefix_id');
                  }}
                  options={[
                    { value: '', label: 'Choose Prefix' },
                    ...prefix.map(prefix => ({
                      value: prefix.id,
                      label: prefix.name

                    }))
                  ]}
                  styles={{
                    ...customStyles,
                    control: (base, state) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: errors.prefix_id ? '#ef4444' : '#d1d5db',
                      '&:hover': {
                        borderColor: errors.prefix_id ? '#ef4444' : '#9ca3af'
                      }
                    })
                  }}
                  placeholder="Select Prefix"
                  className={`react-select-container`}
                  classNamePrefix="react-select"
                />
                {errors.prefix_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.prefix_id.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name <span class="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...register('first_name')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.first_name ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter First Name"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name <span class="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...register('last_name')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.last_name ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter Last Name"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Suffix</label>
                  <input
                    type="text"
                    {...register('suffix')}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                    placeholder="Enter Suffix"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <input
                    type="text"
                    {...register('department')}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                    placeholder="Enter Department"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Job Title</label>
                <input
                  type="text"
                  {...register('job_title')}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  placeholder="Enter Job Title"
                />
              </div>
            </div>
          </div>

          {/* Phone Numbers Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700">Phone Numbers <span class="text-red-500">*</span></label>
              </div>
              <button
                type="button"
                onClick={addPhoneNumber}
                className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>
            {watch('phone_numbers')?.map((phone, index) => (
              <div key={index} className="flex items-center gap-2 mb-3">
                <div className="w-32">
                  <Select
                    name={`phone_numbers.${index}.type`}
                    value={phoneTypeOptions.find(option => option.value === watch(`phone_numbers.${index}.type`))}
                    onChange={(selectedOption) => {
                      setValue(`phone_numbers.${index}.type`, selectedOption?.value || '');
                    }}
                    options={phoneTypeOptions}
                    styles={customStyles}
                    placeholder="Select Type"
                    className={`react-select-container ${errors.phone_numbers?.[index]?.type ? 'border-red-500' : 'border-gray-300'}`}
                    classNamePrefix="react-select"
                  />
                  {errors.phone_numbers?.[index]?.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone_numbers[index].type.message}</p>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="tel"
                    placeholder="(xxx) xxx-xxxx"
                    maxLength="14"
                    {...register(`phone_numbers.${index}.number`, {
                      required: 'Phone number is required',
                      onChange: (e) => handlePhoneInput(e, index)
                    })}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.phone_numbers?.[index]?.number ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.phone_numbers?.[index]?.number && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone_numbers[index].number.message}</p>
                  )}
                </div>
                <div className="w-24">
                  <input
                    type="text"
                    placeholder="EXT"
                    {...register(`phone_numbers.${index}.ext`, {
                      required: 'Extension is required'
                    })}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.phone_numbers?.[index]?.ext ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.phone_numbers?.[index]?.ext && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone_numbers[index].ext.message}</p>
                  )}
                </div>
                {index !== 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePhoneNumber(index)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-full"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Email Addresses Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700">Email Addresses <span class="text-red-500">*</span></label>
              </div>
              <button
                type="button"
                onClick={addEmail}
                className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>
            {watch('emails')?.map((email, index) => (
              <div key={index} className="flex items-center gap-2 mb-3">
                <div className="w-32">
                  <Select
                    name={`emails.${index}.type`}
                    value={emailTypeOptions.find(option => option.value === watch(`emails.${index}.type`))}
                    onChange={(selectedOption) => {
                      setValue(`emails.${index}.type`, selectedOption?.value || '');
                    }}
                    options={emailTypeOptions}
                    styles={customStyles}
                    placeholder="Select Type"
                    className={`react-select-container ${errors.emails?.[index]?.type ? 'border-red-500' : 'border-gray-300'}`}
                    classNamePrefix="react-select"
                  />
                  {errors.emails?.[index]?.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.emails[index].type.message}</p>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="email"
                    placeholder="Enter Email Address"
                    {...register(`emails.${index}.address`, {
                      required: 'Email address is required'
                    })}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.emails?.[index]?.address ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.emails?.[index]?.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.emails[index].address.message}</p>
                  )}
                </div>
                {index !== 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(index)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-full"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Notes Section */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Internal/Private Notes</label>
                <textarea
                  {...register('private_notes')}
                  rows="3"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  placeholder="Enter Internal/Private Notes"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Public/Inventory Order Notes</label>
                <textarea
                  {...register('order_notes')}
                  rows="3"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  placeholder="Enter Public/Inventory Order Notes"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  {id ? 'Update' : 'Save'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorForm; 